<template>
    <section v-if="modele_analyse" :class="{ 'results-inline': !!entryContext }">
        <fieldset :disabled="!!entryContext && (isLoading || batchSaving)" class="results-fields">
        <div class="row results-content">
            <div class="col-lg-12" v-if="container === 'modal' && (isError || (isSuccess && !entryContext)) && message">
                <div :class="`alert alert-${isError ? 'danger' : 'success'} alert-dismissible fade show`" role="alert">
                    {{ message }}
                    <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </div>
            
            <div v-if="isStructured" class="structured-results col-md-12">
                <section v-for="section in sections_a_saisir" :key="section.section_id" class="structured-result-section">
                    <div v-if="!entryContext || sections_a_saisir.length > 1 || !/^r[ée]sultats?$/i.test((section.title || '').trim())" class="structured-section-head">
                        <h5>{{ section.title }}</h5>
                    </div>
                    <div class="structured-fields">
                        <label v-for="field in section.fields" :key="field.field_id" class="structured-field">
                            <span class="structured-field-label">{{ field.label }} <em v-if="field.required">*</em>
                                <strong v-if="referenceAlert(field)" class="reference-alert" :class="'reference-alert--' + referenceAlert(field).tone">
                                    {{ referenceAlert(field).label }}
                                </strong>
                            </span>
                            <textarea v-if="field.type === 'long_text'" v-model="field.value" rows="4" :required="field.required"></textarea>
                            <select v-else-if="field.type === 'choice'" v-model="field.value" :required="field.required">
                                <option value="">Sélectionner</option>
                                <option v-for="option in field.options" :key="option" :value="option">{{ option }}</option>
                            </select>
                            <div v-else-if="field.type === 'boolean'" class="boolean-field">
                                <label><input v-model="field.value" type="radio" :value="true" /> Oui</label>
                                <label><input v-model="field.value" type="radio" :value="false" /> Non</label>
                            </div>
                            <div v-else class="structured-input" :class="{ 'structured-input--with-unit': !!field.unit, 'structured-input--number': field.type === 'number' }">
                                <input v-model="field.value" :type="field.type === 'date' ? 'date' : (field.type === 'number' ? 'number' : 'text')" :step="field.type === 'number' ? 'any' : null" :required="field.required" />
                                <b v-if="field.unit">{{ field.unit }}</b>
                            </div>
                            <small v-if="structuredReference(field)" class="structured-reference">
                                <span class="structured-reference-preview">Valeurs de référence : {{ structuredReference(field) }}</span>
                                <button v-if="structuredReference(field).length > 60" type="button" class="structured-reference-info" :aria-label="'Lire toutes les valeurs de référence de ' + field.label" :aria-expanded="reference_ouverte === field.field_id ? 'true' : 'false'" @click.prevent.stop="basculer_reference(field.field_id)"><i class="fa fa-info-circle" aria-hidden="true"></i></button>
                                <span v-if="reference_ouverte === field.field_id" class="structured-reference-popover" role="tooltip">{{ structuredReference(field) }}</span>
                            </small>
                        </label>
                    </div>
                </section>
            </div>

            <div v-else-if="!isDocumentLayout" class="results-section col-md-12">
                <table class="w-100 results-table">
                    <thead v-if="modele_analyse && modele_analyse.meta && modele_analyse.meta.table_header_name">
                        <tr class="mb-2">
                            <th :colspan="headerRowLength" class="w-100 text-center">
                                {{ modele_analyse.meta.table_header_name }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr 
                            v-for="(row, rowIndex) in (resultats_analyses && resultats_analyses.data)"
                            :key="rowIndex" 
                            :class="{ 'bold-first-row': rowIndex === 0 }"
                        >
                            <template v-for="(cell, cellIndex) in row.cells">
                            <td
                                v-if="!cell.merged" class="w-100 p-0" :key="cellIndex" :rowspan="cell.rowspan || 1" :colspan="cell.colspan || 1"
                                :class="{'has-content': cell.value && cell.value.trim().length > 0 }"
                                @click="cellClickHandler(row, cell, cellIndex)"
                            >
                                <template v-if="!cell.subcells || cell.subcells.length === 0">
                                    <button v-if="cellIndex === 0 && rowIndex > 0 && !row.cells.some(part => part.merged || part.rowspan > 1 || part.colspan > 1)" class="mx-1 remove-row" @click="removeRow($event, rowIndex)">
                                        <b-icon icon="x" variant="danger"></b-icon>
                                    </button>
                                    <span style="min-height: 36px;" v-html="removeParagraph(cell.value)"></span>
                                </template>

                                <div v-else :class="cell.horizontal ? 'd-flex flex-row' : 'd-flex flex-column'">
                                    <div v-for="(subcell, subcellIndex) in cell.subcells"
                                        :key="subcellIndex" class="w-100"
                                        :style="{
                                            'background-color': subcell.value && subcell.value.trim().length > 0 ? '#F2F8FF' : '',
                                            borderRight: cell.horizontal && subcellIndex < cell.subcells.length - 1 ? '1px solid #87A0C7' : '',
                                            borderBottom: !cell.horizontal && subcellIndex < cell.subcells.length - 1 ? '1px solid #87A0C7' : '',
                                        }"
                                        @click="subcellClickHandler($event, subcell, cellIndex, subcellIndex)">
                                        
                                        <span style="min-height: 34px;" v-html="removeParagraph(subcell.value)" class="d-flex align-items-center justify-content-center"></span>
                                    </div>
                                </div>
                            </td>
                            </template>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        </fieldset>
        <div class="row mb-2 mx-1" v-if="resultats_analyses.demande_analyse_id && attachmentsEnabled">
            <div class="col-md-12">
                <AnalyseResultDocuments ref="documents" :demande-id="resultats_analyses.demande_analyse_id" :readonly="submitted || batchSaving" />
            </div>
        </div>

        <div
            class="modal fade"
            :class="{'results-editor' : container === 'modal'}"
            tabindex="-1"
            :id="`resultats-analyse-modal-${this.uniqueRef}`"
            role="dialog"
            aria-hidden="true"
            data-bs-backdrop="false"
            aria-labelledby="resultats-analyse-modal"
        >
            <div class="modal-dialog modal-dialog-centered modal-xl">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="resultats-analyse-modal">
                            Saisie de données | <span class="text-primary" v-html="removeParagraph(selected_row_label)"></span>
                        </h5>
                        <button type="button" class="close" @click="closeResultEditorModal($event)">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <ClipBoard />
                        <vue-editor 
                            v-model="currentCell.value" 
                            :editorToolbar="customToolbar" 
                            ref="e_editor"
                        />

                        <div class="ui-modal-actions row justify-content-end mt-4">
                            <button type="button" class="btn btn-sm btn-secondary-light-o btn-radius mr-2" @click="closeResultEditorModal($event)">Fermer</button>
                            <button type="button" class="btn btn-sm btn-primary btn-radius px-4" @click="closeResultEditorModal($event)">Valider</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="results-footer">
            <div class="analysis-date">
                <label :for="'datetime-' + uniqueRef">Date d’analyse</label>
                <div class="analysis-date-controls">
                    <input type="datetime-local" :id="'datetime-' + uniqueRef" :value="analysisDateInput" :max="analysisDateMax" :disabled="isLoading || submitted || batchSaving" :aria-invalid="analysisDateError ? 'true' : 'false'" :aria-describedby="analysisDateError ? 'datetime-error-' + uniqueRef : null" @input="setAnalysisDate($event.target.value)" />
                    <button type="button" class="analysis-date-now" :disabled="isLoading || submitted || batchSaving" @click="useCurrentDate">Maintenant</button>
                </div>
                <small v-if="analysisDateError" :id="'datetime-error-' + uniqueRef" class="analysis-date-error" role="alert">{{ analysisDateError }}</small>
            </div>
        <div v-if="!entryContext" class="ui-modal-actions results-save-actions">
            <div v-if="!entryContext" class="mr-2">
                <button type="button" class="btn btn-secondary-light-o btn-radius" @click="goBack">
                    Retour
                </button>
            </div>
            <!-- <div v-if="showPrintButton">
                <button 
                    type="button" 
                    class="btn btn-success btn-radius" 
                    @click="printResultatAnalyse()" 
                    :disabled="is_printing"
                >
                    <span><i class="fa fa-print"></i> Imprimer &nbsp;</span>
                    <span v-if="is_printing" class="spinner-border spinner-sz-1"></span>
                </button>                
            </div> -->
            <div>
                <button type="button" class="btn btn-primary btn-radius" @click="saveResults" :disabled="isLoading || submitted">
                    {{ entryContext ? (results_mode === 'edit' ? 'Enregistrer les modifications' : 'Enregistrer ce résultat') : ((isSuccess || results_mode === 'edit') ? 'Modifier' : 'Enregistrer') }}
                    <div class="spinner-border text-white" v-if="isLoading" role="status">
                        <span class="sr-only ">Loading...</span>
                    </div>
                </button>
            </div>
        </div>
        </div>

    </section>
</template>

<script>
import $ from 'jquery';
// import moment from 'moment';
import { showToast } from '@/utils/notifications';

import { VueEditor } from "vue2-editor";
import { vueEditorToolbar } from '@/utils/constants';
import ClipBoard from '@/components/clipboard';
import AnalyseResultDocuments from './AnalyseResultDocuments.vue';
import { createResultPayload, normalizeAnalysisMeta, technicianEntrySections } from '@/utils/analysis-model-schema.mjs';
import { classifyResultValue, referenceLabel } from '@/utils/analysis-result-reference.mjs';

// import ResultsPDFVersion from '../components/Resultats-version-PDF.vue';

function localDateTime(value = new Date()) {
    // Les dates SQL de l'API sont locales ; ne pas leur appliquer un décalage UTC.
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(value)) return value.replace(' ', 'T').slice(0, 16);
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = number => String(number).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default {
    name: 'ResultatsAnalysesFormTable',
    props: {
        container: String,
        // Saisie groupée : contexte isolé par demande, sans remplacer le magasin
        // partagé utilisé par le registre et les fenêtres de saisie individuelles.
        entryContext: { type: Object, default: null },
        batchSaving: { type: Boolean, default: false },
    },
    components: {
        VueEditor, ClipBoard, AnalyseResultDocuments,
    },

    data() {
        return {
            customToolbar: vueEditorToolbar,
            analysisDateInput: '',
            analysisDateError: '',
            analysisDateMax: localDateTime(),
            currentCell: {
                value: null,
                editable: true,
                subcells: [],
            },
            resultats_analyses: {
                meta: {},
                data: [],
                comment: '',
                recorded_at: null,
                show_cachet: true,
                show_signature: true,
                model_analyse_id: null,
                bulletin_analyse_id: null,
                demande_analyse_id: null,
            },
            isLoading: false,
            submitted: false,
            isSuccess: false,
            isError: false,
            showPrintButton: false,
            message: '',
            uniqueRef: `result-${this._uid}`,
            localSavedResult: null,
            initialSnapshot: '',
            can_print: false,
            selected_row_label: "",
            reference_ouverte: null
        }
    },

    computed: {
        patient_meta_data() {
            if (this.entryContext) return this.entryContext.patient;
            return this.$store.getters.patientMetaData
        },

        modele_analyse() {
            if (this.entryContext) return this.entryContext.model;
            return this.$store.getters.current_modele_analyse
        },

        headerRowLength() {
            let maxLength = 0;
            if(this.results_mode === 'add' && this.modele_analyse !== null) {
                for (const row of ((this.modele_analyse && this.modele_analyse.meta && this.modele_analyse.meta.rows) || [])) {
                    maxLength = Math.max(maxLength, row.cells.length);
                }
            } 
            if(this.results_mode === 'edit') {
                const rows = ((this.resultats_analyses && this.resultats_analyses.data) || []);
                for (const row of rows) {
                    maxLength = Math.max(maxLength, row.cells.length);
                }
            }
            return maxLength;
        },

        isStructured() {
            return normalizeAnalysisMeta(this.modele_analyse && this.modele_analyse.meta).layout === 'structured';
        },
        sections_a_saisir() {
            return technicianEntrySections(this.resultats_analyses.data);
        },
        isDocumentLayout() {
            return normalizeAnalysisMeta(this.modele_analyse && this.modele_analyse.meta).layout === 'document';
        },
        attachmentsEnabled() {
            return normalizeAnalysisMeta(this.modele_analyse && this.modele_analyse.meta).attachments_enabled;
        },

        saved_resultats() {
            if (this.entryContext) return this.localSavedResult || this.entryContext.result;
            return this.$store.getters.resultatsAnalyse
        },

        results_mode() {
            if (this.entryContext) return this.saved_resultats && this.saved_resultats.id ? 'edit' : 'add';
            return this.$store.getters.resultsMode;
        },

        hasUnsavedChanges() {
            return !!this.analysisDateError || (!!this.initialSnapshot && JSON.stringify(this.resultats_analyses) !== this.initialSnapshot);
        },

        user() {
            return this.$store.getters['authInfo'];
        },

        activeRole() {
            return this.$store.getters['activeRole'];
        },
    },

    watch: {
        'resultats_analyses.recorded_at': function (newValue) {
            if (!this.analysisDateError) this.analysisDateInput = localDateTime(newValue || new Date());
        },

        modele_analyse: function () {
            this.initializeResults();
        },

        saved_resultats: function () {
            if(this.saved_resultats && !this.isSuccess) {
                this.initializeResults();
            }
        }
    },

    mounted() {
        // if(!this.container || this.container !== 'modal') {
        //     this.initializeResults();
        // }
        this.initializeResults();
    },

    methods: {
        hasResultInput() {
            if (this.$refs.documents && this.$refs.documents.documents.length) return true;
            if (this.isStructured) return this.sections_a_saisir.some(section => section.fields.some(field => field.value !== null && field.value !== undefined && String(field.value).trim() !== ''));
            if (this.isDocumentLayout || !this.initialSnapshot) return false;
            return JSON.stringify(this.resultats_analyses.data) !== JSON.stringify(JSON.parse(this.initialSnapshot).data);
        },

        validateBeforeSave() {
            if (this.analysisDateError) return false;
            if (this.$refs.documents && this.$refs.documents.uploading) { this.isError = true; this.message = 'Attendez la fin du transfert des documents.'; return false; }
            if (this.isDocumentLayout && this.resultats_analyses.demande_analyse_id && !(this.$refs.documents && this.$refs.documents.documents.length)) { this.isError = true; this.message = 'Joignez un document avant d’enregistrer le résultat.'; return false; }
            if (this.isStructured && !this.validateStructuredResults()) {
                this.isError = true;
                this.message = 'Renseignez tous les résultats obligatoires.';
                if(this.container !== 'modal') showToast(this.message, 'error');
                return false;
            }
            return true;
        },

        async saveResults() {
            if (this.isLoading || this.submitted) return false;
            if (!this.validateBeforeSave()) return false;
            this.isLoading = true;
            this.isError = false;
            let saved = false;

            const analyseId = this.modele_analyse.analyse.id;
            

            try {
                let response = null;

                // LAB-004 : toutes les mises en page passent par les routes de résultats existantes, avec la ligne de demande
                if(this.isSuccess || this.results_mode === 'edit') {
                    const updatedResults = {
                        ...this.saved_resultats,
                        resultat_id: this.saved_resultats.id,
                        demande_analyse_id: this.resultats_analyses.demande_analyse_id,
                        data: this.resultats_analyses.data,
                        meta: {
                            ...(this.saved_resultats.meta || {}),
                            ...(this.resultats_analyses.meta || {}),
                            doctor_fullname: this.patient_meta_data.doctor_fullname,
                            methodology_applied: this.modele_analyse.analyse.technique_utilisee,
                        },
                        recorded_at: this.resultats_analyses.recorded_at,
                        comment: this.resultats_analyses.comment,
                    }
                    response = await this.$store.dispatch("updateResultatsAnalyse", updatedResults);
                } else {

                    if (this.resultats_analyses && this.resultats_analyses.recorded_at !== undefined && this.resultats_analyses.recorded_at !== null) {
                        this.resultats_analyses.recorded_at = this.resultats_analyses.recorded_at.replace('T', ' ');
                    }
                    this.resultats_analyses.analyse_id = analyseId
                    
                    this.resultats_analyses.meta.methodology_applied = this.modele_analyse.analyse.technique_utilisee
                    
                    response = await this.$store.dispatch("saveResultatsAnalyse", { analyseId, data: this.resultats_analyses });
                }

                this.isLoading = false;

                if (response && (response.status === 201 || response.status === 200)) {
                    saved = true;
                    this.isSuccess = true;
                    this.showPrintButton = true;
                    
                    this.message = `Résultats d'analyse ${this.saved_resultats ? 'mis à jour' : 'enregistré'} avec succès`;

                    if (this.entryContext) {
                        this.localSavedResult = JSON.parse(JSON.stringify(response.data));
                        this.initialSnapshot = JSON.stringify(this.resultats_analyses);
                    } else {
                        this.$store.dispatch("setResultatsAnalyse", (response && response.data));
                        this.$store.dispatch("setResultsMode", "edit");
                    }

                    this.$emit("onResultSavedSuccessfully");

                    if(this.container !== 'modal') {
                        showToast(this.message, 'success');
                    }
                } else { this.isError = true; this.message = 'L’enregistrement n’a pas été confirmé. Veuillez réessayer.'; }
            } catch (error) {
                this.isLoading = false;
                this.isError = true;
                this.message = (error.response && error.response.data && error.response.data.message) || "Erreur lors de la sauvegarde. Veuillez réessayer.";
            }

            if (!this.entryContext) setTimeout(() => {
                this.isSuccess = false;
                this.isError = false;
                this.message = '';
            }, 5000);
            return saved;
        },

        cellClickHandler(row, cell) {
            this.selected_row_label = row.cells[0].value
            this.currentCell = cell;

            $(`#resultats-analyse-modal-${this.uniqueRef}`).modal('show');
            
            setTimeout(() => {
                this.$refs.e_editor.quill.focus()
            }, 500);
        },

        subcellClickHandler(event, subcell) {
            event.stopPropagation();

            this.currentCell = subcell;
            $(`#resultats-analyse-modal-${this.uniqueRef}`).modal('show');
        },
        removeParagraph(text) {
            return text && text.replace(/<p>/g, '').replace(/<\/p>/g, '');
        },

        initializeResults() {
            
            const normalizedMeta = normalizeAnalysisMeta(this.modele_analyse && this.modele_analyse.meta);
            this.resultats_analyses.data = this.results_mode === 'add'
                ? createResultPayload(normalizedMeta)
                : JSON.parse(JSON.stringify((this.saved_resultats && this.saved_resultats.data) || []));
            this.resultats_analyses.comment =  this.results_mode === 'add' ? '' : (this.saved_resultats && this.saved_resultats.comment);
            this.resultats_analyses.model_analyse_id = (this.modele_analyse && this.modele_analyse.id);

            this.resultats_analyses.bulletin_analyse_id = this.patient_meta_data.bulletin_analyse_id;
            this.resultats_analyses.demande_analyse_id = this.patient_meta_data.demande_analyse_id || null;

            let biologiste_title = ''
            biologiste_title = (this.activeRole.nom == "ROLE_MEDECIN_BIOLOGISTE")?'Le médecin biologiste':(this.activeRole.nom == "ROLE_TECH_SUP_LABO")?'Le Technicien Supérieur de Laboratoire':''

            this.resultats_analyses.meta = {
                table_header_name: (this.modele_analyse && this.modele_analyse.meta && this.modele_analyse.meta.table_header_name),
                schema_version: normalizedMeta.schema_version,
                layout: normalizedMeta.layout,
                biologiste_id: this.user.id,
                biologiste_title: biologiste_title,
                biologiste_fullname: this.user.name,
                biologiste_email: this.user.email,
                patient_id: (this.patient_meta_data.patient && this.patient_meta_data.patient.patient_id),
                ...this.resultats_analyses.meta,
                ...this.patient_meta_data,
            }

            if(this.results_mode === 'edit' && this.saved_resultats) {
                this.resultats_analyses.recorded_at = (this.saved_resultats && this.saved_resultats.recorded_at);
                this.resultats_analyses.meta.doctor_fullname = this.patient_meta_data.doctor_fullname

            }
            this.analysisDateInput = localDateTime(this.resultats_analyses.recorded_at || new Date());
            this.analysisDateMax = localDateTime();
            this.analysisDateError = '';
            this.isSuccess = false;
            this.initialSnapshot = JSON.stringify(this.resultats_analyses);
        },

        setAnalysisDate(value) {
            if (this.isLoading || this.submitted || this.batchSaving) return;
            this.analysisDateInput = value;
            this.analysisDateMax = localDateTime();
            const date = new Date(value);
            this.analysisDateError = !value || Number.isNaN(date.getTime())
                ? 'Renseignez une date et une heure valides.'
                : (date.getTime() > Date.now() ? 'La date d’analyse ne peut pas être dans le futur.' : '');
            if (!this.analysisDateError) this.resultats_analyses.recorded_at = value;
        },

        useCurrentDate() {
            this.setAnalysisDate(localDateTime());
        },

        closeResultEditorModal(event) {
            event.preventDefault();
            event.stopPropagation();

            $(`#resultats-analyse-modal-${this.uniqueRef}`).modal('hide');
        },

        goBack() {
            if(this.container === 'modal') {
                this.$emit('closeModal')
            }else{
                this.$router.go(-2)
            }
        },

        removeRow(event, index) {
            event.preventDefault();
            event.stopPropagation();
            if (this.resultats_analyses.data[index].cells.some(cell => cell.merged || cell.rowspan > 1 || cell.colspan > 1)) return;
            this.resultats_analyses.data.splice(index, 1);
        },

        validateStructuredResults() {
            return technicianEntrySections(this.resultats_analyses.data).every(section =>
                section.fields.every(field => !field.required || String(field.value === null || field.value === undefined ? '' : field.value).trim().length > 0)
            );
        },

        structuredReference(field) {
            return referenceLabel(field);
        },

        basculer_reference(id) {
            this.reference_ouverte = this.reference_ouverte === id ? null : id;
        },

        referenceAlert(field) {
            return field.type === 'number' ? classifyResultValue(field.value, field.reference) : null;
        },

        generateFilename(patientFullname) {
            const timestamp = new Date().getTime();
            const sanitizedFullname = patientFullname.replace(/ /g, '_');
            return `Resultats-analyse-${sanitizedFullname}-${timestamp}.pdf`;
        },
        
        printResultatAnalyse() {
            this.$emit('emitSetResult', this.saved_resultats)
        },
    }
}
</script>

<style src="../styles/resultats-analyse.scss" lang="scss" scoped></style>
